import React from 'react';
import {
  ModalFooter,
  Button,
  HStack,
  Spacer,
  Tooltip,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { InsuranceDetailFormValues } from '../../../../types/Form';
import useAddInsuranceModalContext from '../../hooks/useAddInsuranceModalContext';
import { InsurancePolicyModalProps } from '../../hooks/useInsurancePolicyModal';

interface Props {
  onClose: () => void;
  isDisabled: boolean;
  insuranceModalProps: InsurancePolicyModalProps;
}

const AddInsuranceModalInsuranceFooter: React.FC<Props> = ({
  onClose,
  isDisabled,
  insuranceModalProps,
}) => {
  const { page, setPage, coverages } = insuranceModalProps;
  const { riskObjects, insuranceCompanies } = useAddInsuranceModalContext();
  const { trigger, setValue, getValues, watch } =
    useFormContext<InsuranceDetailFormValues>();
  const { t } = useTranslation();

  const handleClick = async () => {
    const isNameValid = await trigger('name', { shouldFocus: true });
    const isCompanyValid = await trigger('insurance_company', {
      shouldFocus: true,
    });
    if (isCompanyValid && isNameValid) {
      if (riskObjects.length || insuranceCompanies.length) {
        coverages.forEach((field, i) => {
          // Prefill riskObject when it was not yet set
          if (!field.risk_object && riskObjects.length === 1) {
            setValue(`coverages.${i}.risk_object_id`, riskObjects[0].value);
          }
          // Prefill insurance company when it was not yet set
          if (!field.insurance_company && insuranceCompanies.length === 1) {
            setValue(
              `coverages.${i}.insurance_company`,
              insuranceCompanies[0].value,
            );
          }
        });
      }
      // Prefill the coverage with all the insurance data when there is only one coverage
      if (coverages.length === 1) {
        const fields = ['premium', 'payment_interval'] as const;
        // loop over the fields and copy the data
        fields.forEach((fieldName) => {
          if (!coverages[0][fieldName] || fieldName === 'payment_interval') {
            setValue(`coverages.0.${fieldName}`, getValues(fieldName));
          }
        });
      }
      setPage({ type: 'COVERAGE', index: 0 });
    }
  };

  return (
    <ModalFooter as={HStack}>
      <Spacer />
      {page.index !== 0 && (
        <Button variant="grayedGhost" onClick={onClose}>
          {t('CANCEL')}
        </Button>
      )}
      <Tooltip
        isDisabled={!isDisabled}
        label={t(
          'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_TOOLTIP_MIN-COVERAGES',
        )}
      >
        <Button onClick={handleClick} isDisabled={isDisabled}>
          {(watch('coverages') || [])?.length > 1
            ? t(
                'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_ADD-COVERAGES',
              )
            : t(
                'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_ADD-COVERAGE',
              )}
        </Button>
      </Tooltip>
    </ModalFooter>
  );
};

export default AddInsuranceModalInsuranceFooter;
